/**
 * The cull corridor's indifferent machinery.
 *
 * Beat 5 is not a scare scene. The cycle, plastic and bin all do their work
 * without noticing Rosalie, so this set avoids calls, rises and tails that would
 * make the room appear to be responding to her.
 */

import { connectChain, filter, gain, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/**
 * Close a gain envelope explicitly at both ends.
 *
 * Web Audio sources keep running after the offline render has finished; setting
 * their gain back to zero is therefore part of the sound, not housekeeping. A
 * non-zero last sample is a click at the next loop and a one-shot tail that the
 * player will hear as invented drama.
 */
function shapedPulse(
  shape: GainNode,
  t0: number,
  start: number,
  attack: number,
  hold: number,
  release: number,
  level: number,
): void {
  shape.gain.setValueAtTime(0, t0 + start);
  shape.gain.linearRampToValueAtTime(level, t0 + start + attack);
  shape.gain.setValueAtTime(level, t0 + start + attack + hold);
  shape.gain.linearRampToValueAtTime(0, t0 + start + attack + hold + release);
}

/**
 * The fixed 4.2-second cycle behind the sheeting.
 *
 * The long gap is structural: the corridor needs enough emptiness that the
 * thump and hiss can become a clock without becoming a cue to solve. Both paths
 * meet actual zero at the wrap, including the otherwise continuous bed, because
 * this loop will repeat for minutes and the seam matters more than its texture.
 */
const plantCycle: SoundDefinition = {
  id: 'plant-cycle',
  label: 'Cull plant cycle',
  description: 'A damped low thump, released-air hiss and deliberately long machine pause.',
  category: 'machine',
  duration: 4.2,
  loop: true,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.27);
    connectChain(out, ctx.destination);

    const thump = gain(ctx, 0);
    const thumpOsc = ctx.createOscillator();
    thumpOsc.type = 'sine';
    thumpOsc.frequency.value = 63;
    // The 28 ms rise deliberately denies the hit a hard leading edge. A click
    // would turn a press doing routine work into a jump-scare punctuation mark.
    shapedPulse(thump, t0, 0, 0.028, 0.035, 0.48, 0.16);
    connectChain(thumpOsc, thump, out);
    thumpOsc.start(0);

    const hiss = gain(ctx, 0);
    shapedPulse(hiss, t0, 0.35, 0.006, 0.035, 0.46, 0.11);
    connectChain(
      noiseSource(ctx, 4.2, 601),
      // Air is intentionally broad enough to locate at range, but band-limited
      // away from both steam-whistle brightness and the bass belonging to the hit.
      filter(ctx, { type: 'highpass', frequency: 900, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 2200, q: 0.7 }),
      hiss,
      out,
    );

    const bed = gain(ctx, 0);
    // A quiet machine remains in the pause, not an ambient pad. The final 15 ms
    // are explicitly silent rather than merely arriving at zero on the nominal
    // endpoint: an OfflineAudioContext's final sample is just before 4.2 s, so
    // ending the ramp there would leave a small but real seam step every loop.
    bed.gain.setValueAtTime(0, t0);
    bed.gain.linearRampToValueAtTime(0.017, t0 + 0.015);
    bed.gain.setValueAtTime(0.017, t0 + 4.17);
    bed.gain.linearRampToValueAtTime(0, t0 + 4.185);
    connectChain(
      noiseSource(ctx, 4.2, 607),
      filter(ctx, { type: 'lowpass', frequency: 150, q: 0.7 }),
      bed,
      out,
    );
  },
};

/** Dry strip-curtain movement, kept deliberately unmemorable over fifty repeats. */
const sheetingRustle: SoundDefinition = {
  id: 'sheeting-rustle',
  label: 'Plastic sheeting rustle',
  description: 'Short, dry, irregular plastic-strip movement with no pitched tail.',
  category: 'machine',
  duration: 0.45,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.19);
    connectChain(out, ctx.destination);

    const texture = gain(ctx, 0);
    // Irregular, overlapping grains avoid a signature flick shape. A repeated
    // single scrape would train the ear to wait for it every plant cycle, which
    // is the opposite of the curtain being incidental industrial material.
    const grains: readonly [number, number, number, number][] = [
      [0.012, 0.003, 0.026, 0.048],
      [0.067, 0.002, 0.018, 0.035],
      [0.119, 0.004, 0.041, 0.054],
      [0.188, 0.002, 0.022, 0.041],
      [0.253, 0.005, 0.031, 0.046],
      [0.318, 0.003, 0.019, 0.029],
    ];
    for (const [offset, attack, release, level] of grains) {
      shapedPulse(texture, t0, offset, attack, 0.001, release, level);
    }
    texture.gain.setValueAtTime(0, t0 + 0.45);
    connectChain(
      noiseSource(ctx, 0.45, 613),
      // The narrow mid-high band is brittle plastic, not a fabric swish or a
      // high glassy crack. Nothing pitched remains once the strip falls back.
      filter(ctx, { type: 'highpass', frequency: 1250, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 4700, q: 0.7 }),
      texture,
      out,
    );
  },
};

/**
 * The bin ride, heard from inside a loaded plastic container.
 *
 * This is foley rather than a machine bed: its source is the bin touching the
 * floor and it only exists during the carried shot. It contains no regular
 * impacts because playback rate follows the bin's speed; a rhythm would sweep
 * visibly and make the prop feel synthetic.
 */
const binCastors: SoundDefinition = {
  id: 'bin-castors',
  label: 'Loaded bin castors',
  description: 'Close, boxy wheel rumble with dense, non-periodic concrete chatter.',
  category: 'foley',
  duration: 1.2,
  loop: true,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.22);
    connectChain(out, ctx.destination);

    const rumble = gain(ctx, 0);
    rumble.gain.setValueAtTime(0, t0);
    rumble.gain.linearRampToValueAtTime(0.11, t0 + 0.012);
    // Leave the last 12 ms empty. The transport will wrap from the final
    // rendered sample, not from the ideal 1.2-second timestamp, and this guard
    // makes the close, enclosed rumble genuinely seamless rather than merely
    // close enough at the AudioParam boundary.
    rumble.gain.setValueAtTime(0.11, t0 + 1.176);
    rumble.gain.linearRampToValueAtTime(0, t0 + 1.188);
    connectChain(
      noiseSource(ctx, 1.2, 619),
      // A low, resonant band is the bin cavity around the listener. It supplies
      // load without adding an audible motor note that would pitch-warp in motion.
      filter(ctx, { type: 'bandpass', frequency: 185, q: 0.8 }),
      rumble,
      out,
    );

    const chatter = gain(ctx, 0);
    const grains: readonly [number, number, number, number][] = [
      [0.031, 0.003, 0.017, 0.026],
      [0.084, 0.002, 0.013, 0.019],
      [0.141, 0.004, 0.021, 0.032],
      [0.226, 0.002, 0.015, 0.022],
      [0.291, 0.003, 0.026, 0.029],
      [0.367, 0.002, 0.012, 0.018],
      [0.449, 0.003, 0.019, 0.027],
      [0.532, 0.002, 0.015, 0.021],
      [0.611, 0.004, 0.024, 0.03],
      [0.694, 0.002, 0.013, 0.018],
      [0.773, 0.003, 0.022, 0.025],
      [0.851, 0.002, 0.014, 0.02],
      [0.934, 0.004, 0.027, 0.029],
      [1.012, 0.002, 0.016, 0.021],
      [1.089, 0.003, 0.019, 0.024],
    ];
    for (const [offset, attack, release, level] of grains) {
      shapedPulse(chatter, t0, offset, attack, 0.001, release, level);
    }
    chatter.gain.setValueAtTime(0, t0 + 1.2);
    connectChain(
      noiseSource(ctx, 1.2, 631),
      // The top end stops before a clean wheel tick; these are many tiny rough
      // contacts reverberating inside plastic, never a countable wheel rotation.
      filter(ctx, { type: 'bandpass', frequency: 740, q: 0.85 }),
      filter(ctx, { type: 'lowpass', frequency: 1550, q: 0.7 }),
      chatter,
      out,
    );
  },
};

/**
 * The closure outside the bin.
 *
 * This is foley because it is one physical lid interaction, not an ambience
 * layer. Its short body and smaller high edge make the action legible without
 * giving the final outside sound a dramatic tail or a horror-film slam.
 */
const binLidClose: SoundDefinition = {
  id: 'bin-lid-close',
  label: 'Bin lid close',
  description: 'One soft, heavy plastic-and-steel bin closure with a brief dry edge.',
  category: 'foley',
  duration: 0.8,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    // This is the largest isolated event in the set, but body-heavy gain makes
    // it louder by mass rather than by the sharp top-end transient of a sting.
    const out = gain(ctx, 0.35);
    connectChain(out, ctx.destination);

    const body = gain(ctx, 0);
    shapedPulse(body, t0, 0.035, 0.014, 0.018, 0.29, 0.28);
    connectChain(
      noiseSource(ctx, 0.8, 641),
      // The body is intentionally noise-shaped rather than a low oscillator:
      // a resonant pitch would make an ordinary lid read as a theatrical boom.
      filter(ctx, { type: 'lowpass', frequency: 390, q: 0.85 }),
      body,
      out,
    );

    const edge = gain(ctx, 0);
    shapedPulse(edge, t0, 0.049, 0.002, 0.003, 0.042, 0.06);
    connectChain(
      noiseSource(ctx, 0.8, 647),
      // Steel is only a small rim catch above the soft plastic mass. Extending
      // it into a ring would turn this unremarkable closure into a sting.
      filter(ctx, { type: 'bandpass', frequency: 2650, q: 1.15 }),
      edge,
      out,
    );
  },
};

export const cullSounds: readonly SoundDefinition[] = [
  plantCycle,
  sheetingRustle,
  binCastors,
  binLidClose,
];
